import { properties as seedProperties, structures as seedStructures, budgetSettings as seedBudgetSettings, budgetCategories as seedBudgetCategories } from "../data/seed";
import type { PropertyRecord } from "../types/portfolio";
import type { Structure, InvestorProfile } from "../types/entities";
import type { BudgetSettings, BudgetCategory, BudgetGroup, BudgetBucket, IncomeRow } from "../types/budget";

const KEYS = {
  properties: "ppa:properties",
  structures: "ppa:structures",
  profile: "ppa:profile",
  budget: "ppa:budget",
  chatMessages: "ppa:chat:messages",
} as const;

// ─── Chat history persistence (Day 36 — VISION gap #4) ───────────────────────
// localStorage-only for V1. The dual-write upgrade path to a Supabase
// chat_messages table is documented in HANDOVER and ARCHITECTURE §6 if
// cross-device sync becomes a real requirement.

export interface ChatMessage {
  id: string;
  role: "user" | "assistant";
  content: string;
  createdAt: number; // epoch ms — used for the 7-day retention prune
}

const CHAT_MAX_MESSAGES = 50;
const CHAT_MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000; // 7 days

export function loadChatHistory(): ChatMessage[] {
  try {
    const raw = localStorage.getItem(KEYS.chatMessages);
    if (raw === null) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    const cutoff = Date.now() - CHAT_MAX_AGE_MS;
    // Defensive validation — drop malformed entries and prune to retention window.
    return parsed
      .filter((m): m is ChatMessage =>
        m && typeof m === "object"
        && typeof m.id === "string"
        && (m.role === "user" || m.role === "assistant")
        && typeof m.content === "string"
        && typeof m.createdAt === "number"
        && m.createdAt >= cutoff
      );
  } catch {
    return [];
  }
}

export function saveChatHistory(messages: ChatMessage[]): void {
  try {
    // Cap at last N messages on save — older messages drop off naturally.
    const trimmed = messages.slice(-CHAT_MAX_MESSAGES);
    localStorage.setItem(KEYS.chatMessages, JSON.stringify(trimmed));
  } catch {
    // localStorage unavailable or quota exceeded — fail silently. Chat is
    // a working tool, not a record of truth.
  }
}

export function clearChatHistory(): void {
  try {
    localStorage.removeItem(KEYS.chatMessages);
  } catch {
    // ignore
  }
}

function blankProfile(): InvestorProfile {
  return {
    fullLegalName: "",
    preferredName: "",
    dateOfBirth: "",
    gender: "",
    currentAddress: "",
    phone: "",
    email: "",
    tfn: "",
    citizenshipStatus: "",
    taxResidencyStatus: "",
    annualPreTaxIncome: 0,
    employmentType: "payg",
    employerName: "",
    employerAbn: "",
    marginalTaxRate: 0,
    monthlyNetContribution: 0,
    strategyTone: "Balanced",
    reducedIncomePeriods: [],
    householdMembers: [],
    relationships: [],
    verificationDocuments: [],
    taxDocuments: [],
    payslips: [],
  };
}

function load<T>(key: string, fallback: T): T {
  try {
    const raw = localStorage.getItem(key);
    if (raw === null) return fallback;
    return JSON.parse(raw) as T;
  } catch {
    return fallback;
  }
}

function save<T>(key: string, value: T): void {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // localStorage unavailable or quota exceeded — fail silently
  }
}

export function loadProperties(): PropertyRecord[] {
  return load(KEYS.properties, seedProperties);
}

export function saveProperties(data: PropertyRecord[]): void {
  save(KEYS.properties, data);
}

export function loadStructures(): Structure[] {
  return load(KEYS.structures, seedStructures);
}

export function saveStructures(data: Structure[]): void {
  save(KEYS.structures, data);
}

export function loadProfile(): InvestorProfile {
  return load(KEYS.profile, blankProfile());
}

export function saveProfile(data: InvestorProfile): void {
  save(KEYS.profile, data);
}

export function loadBudget(): { settings: BudgetSettings; categories: BudgetCategory[] } {
  try {
    const raw = localStorage.getItem(KEYS.budget);
    if (raw === null) {
      return { settings: seedBudgetSettings, categories: seedBudgetCategories };
    }
    const parsed = JSON.parse(raw) as { settings: Record<string, unknown>; categories: unknown[] };
    const s: Record<string, unknown> = parsed.settings ?? {};
    const rawCats = Array.isArray(parsed.categories) ? (parsed.categories as BudgetCategory[]) : seedBudgetCategories;

    // Migrate incomeRows: old single monthlyIncome field → one row
    const incomeRows: IncomeRow[] = Array.isArray(s.incomeRows)
      ? (s.incomeRows as IncomeRow[])
      : typeof s.monthlyIncome === "number" && (s.monthlyIncome as number) > 0
        ? [{ id: "migrated-income", label: "Monthly income", amount: s.monthlyIncome as number, frequency: "M" }]
        : seedBudgetSettings.incomeRows;

    // Migrate buckets: old three separate fields → buckets array, default type to "custom"
    const rawBuckets: BudgetBucket[] = Array.isArray(s.buckets)
      ? (s.buckets as BudgetBucket[])
      : [
          { id: "b-invest",  name: "Investments", type: "investment" as const, monthlyAmount: (s.monthlyInvestmentAllocation as number) ?? 0 },
          { id: "b-smile",   name: "Smile",        type: "lifestyle" as const, monthlyAmount: (s.monthlySmileAllocation as number)     ?? 0 },
          { id: "b-splurge", name: "Splurge",       type: "lifestyle" as const, monthlyAmount: (s.monthlySplurgeAllocation as number)  ?? 0 },
        ];
    const buckets = rawBuckets.map((b) => ({ ...b, type: b.type ?? ("custom" as const) }));

    // Phase 6a: seed property bucket if missing
    if (!buckets.some((b) => b.type === "property")) {
      // Check appSettings in localStorage for personalContributionOverride (takes priority)
      let override: number | undefined;
      try {
        const appRaw = localStorage.getItem("ppa:settings");
        if (appRaw) override = (JSON.parse(appRaw) as Record<string, unknown>).personalContributionOverride as number | undefined;
      } catch {}
      const investBucket = buckets.find((b) => b.type === "investment");
      const amount = override ?? investBucket?.monthlyAmount ?? 0;
      buckets.push({
        id: Date.now().toString(36) + Math.random().toString(36).slice(2),
        name: "Property Portfolio",
        type: "property" as const,
        locked: true,
        monthlyAmount: amount,
        description: "Money allocated from your monthly surplus toward your property portfolio",
      });
      // Demote investment bucket to custom if we used its amount (not the override)
      if (!override && investBucket) {
        const idx = buckets.findIndex((b) => b.id === investBucket.id);
        if (idx >= 0) buckets[idx] = { ...buckets[idx], type: "custom" as const };
      }
      // Clear the override from appSettings localStorage
      if (override !== undefined) {
        try {
          const appRaw = localStorage.getItem("ppa:settings");
          if (appRaw) {
            const parsed = JSON.parse(appRaw);
            delete parsed.personalContributionOverride;
            parsed.propertyBucketSeeded = true;
            localStorage.setItem("ppa:settings", JSON.stringify(parsed));
          }
        } catch {}
      }
    }

    const groups: BudgetGroup[] = Array.isArray(s.groups)
      ? (s.groups as BudgetGroup[])
      : seedBudgetSettings.groups;

    const settings: BudgetSettings = {
      incomeRows,
      groups,
      buckets,
      spareIncomeAvailableForInvesting:
        s.spareIncomeAvailableForInvesting === null || typeof s.spareIncomeAvailableForInvesting === "number"
          ? (s.spareIncomeAvailableForInvesting as number | null)
          : null,
    };

    // Migrate categories: resolve groupId from legacy groupName
    const miscGroupId = groups.find((g) => g.name === "Miscellaneous")?.id ?? "g-12";
    const categories: BudgetCategory[] = rawCats.map((c) => {
      const raw = c as unknown as Record<string, unknown>;
      const groupId = (raw.groupId as string)
        ?? groups.find((g) => g.name === (raw.groupName as string))?.id
        ?? miscGroupId;
      return { ...c, groupId };
    });

    return { settings, categories };
  } catch {
    return { settings: seedBudgetSettings, categories: seedBudgetCategories };
  }
}

export function saveBudget(data: { settings: BudgetSettings; categories: BudgetCategory[] }): void {
  save(KEYS.budget, data);
}
